import { useState } from "react";
import { HelpCircle } from "lucide-react";
import {
  answerCashMovementQuestion,
  dismissCashMovementQuestion,
} from "wasp/client/operations";
import { Button } from "../client/components/ui/button";
import { useToast } from "../client/hooks/use-toast";
import useTranslation from "../client/hooks/useTranslation";

/**
 * Question posée à l'utilisateur sur un mouvement de trésorerie inexpliqué.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TROIS RÈGLES GOUVERNENT CE COMPOSANT, et chacune vient d'un piège concret :
 *
 * 1. LA QUESTION EST OUVERTE. Jamais « avez-vous fait un retrait ? » — qui ne
 *    propose qu'une hypothèse sur six et induit la réponse. Une baisse de solde
 *    sans trade a six causes : retrait, reset, frais de plateforme, frais
 *    d'activation funded, ajustement broker, commission détachée du fill. Un
 *    compte challenge de 50 000 $ reseté produit EXACTEMENT le même signal
 *    qu'un payout de 50 000 $.
 *
 * 2. LE MONTANT EST PRÉ-REMPLI ET MODIFIABLE. Jamais de champ vide à remplir à
 *    la main : moins d'erreurs, moins de friction. Mais modifiable, parce que
 *    l'utilisateur sait ce qui s'est passé mieux que l'inférence.
 *
 * 3. LA FÉLICITATION SUIT LE TYPE DE MOUVEMENT. Un payout confirmé mérite
 *    d'être célébré ; féliciter quelqu'un dont le compte vient d'être reseté
 *    serait un contresens. Le ton suit la réponse, il ne la précède pas — d'où
 *    le drapeau `celebrate` renvoyé par le serveur plutôt qu'un message figé
 *    ici.
 * ─────────────────────────────────────────────────────────────────────────────
 */

type CashQuestion = {
  id: string;
  accountId: string;
  amount: number;
  currency: string;
  observedAt: string | Date;
  reason: string;
};

interface Props {
  question: CashQuestion;
  onSettled: () => void;
}

const CashMovementQuestionCard = ({ question, onSettled }: Props) => {
  const t = useTranslation();
  const { toast } = useToast();

  // Pré-rempli, arrondi pour l'affichage : les flottants du broker portent du
  // bruit en 1e-12 et « 4999.999999999999 » serait illisible.
  const [amount, setAmount] = useState(
    String(Math.round(question.amount * 100) / 100),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const answers = [
    { value: "withdrawal", label: t.tradingAccounts.cashQuestionAnswerWithdrawal },
    { value: "account_reset", label: t.tradingAccounts.cashQuestionAnswerReset },
    { value: "fees", label: t.tradingAccounts.cashQuestionAnswerFees },
    { value: "unknown", label: t.tradingAccounts.cashQuestionAnswerUnknown },
  ] as const;

  const handleAnswer = async (answer: string) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const parsed = Number(amount);
      const result: any = await answerCashMovementQuestion({
        questionId: question.id,
        answer,
        // Envoyé seulement s'il est exploitable : un champ vidé par
        // l'utilisateur ne doit pas écraser le montant observé par un NaN.
        ...(Number.isFinite(parsed) && parsed > 0
          ? { confirmedAmount: parsed }
          : {}),
      });

      toast({
        title: result?.celebrate
          ? t.tradingAccounts.cashQuestionCelebrate
          : t.tradingAccounts.cashQuestionRecorded,
      });
      onSettled();
    } catch {
      toast({ title: t.tradingAccounts.error, variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDismiss = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await dismissCashMovementQuestion({ questionId: question.id });
      onSettled();
    } catch {
      toast({ title: t.tradingAccounts.error, variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="border-border bg-muted/30 space-y-3 rounded-lg border p-3">
      <div className="flex items-start gap-2">
        <HelpCircle className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
        <div className="min-w-0">
          <p className="text-foreground text-sm font-medium">
            {t.tradingAccounts.cashQuestionTitle}
          </p>
          <p className="text-muted-foreground text-xs">
            {t.tradingAccounts.cashQuestionBody}
          </p>
          {/* Distinguer « le log n'a pas pu être lu » de « le log n'explique
              pas » aide l'utilisateur à comprendre pourquoi on lui demande. */}
          {question.reason === "cash_log_unavailable" && (
            <p className="text-muted-foreground mt-1 text-xs italic">
              {t.tradingAccounts.cashQuestionReasonUnavailable}
            </p>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <label className="text-muted-foreground text-xs">
          {t.tradingAccounts.cashQuestionAmountLabel}
        </label>
        <input
          type="number"
          step="0.01"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="border-border bg-background text-foreground w-32 rounded border px-2 py-1 font-mono text-sm"
        />
        <span className="text-muted-foreground text-xs">{question.currency}</span>
      </div>

      {/* Les quatre réponses au même niveau visuel : en mettre une en avant
          reviendrait à suggérer une hypothèse, ce que la question ouverte
          cherche précisément à éviter. */}
      <div className="flex flex-wrap gap-2">
        {answers.map((a) => (
          <Button
            key={a.value}
            size="sm"
            variant="outline"
            disabled={isSubmitting}
            onClick={() => handleAnswer(a.value)}
          >
            {a.label}
          </Button>
        ))}
        <Button
          size="sm"
          variant="ghost"
          disabled={isSubmitting}
          onClick={handleDismiss}
        >
          {t.tradingAccounts.cashQuestionDismiss}
        </Button>
      </div>
    </div>
  );
};

export default CashMovementQuestionCard;
